/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-field/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useApolloClient, useMutation } from '@apollo/client/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';

import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import React, { useState, useEffect, useContext } from 'react';

import { AddFieldProps } from '@components/admin/add-field/add-field.interface';
import CopyButton from '@components/core/copy-button';
import { LocalStateContext } from 'contexts/local-state-context';
import { useBoolean, useTranslation } from 'hooks';
import { SAVE_EXTRA_FIELD } from 'queries/central-monitoring';
import { RowDataSourceTable, FieldDefinitionFieldType } from 'types/graphql-global-types';
import { createArrayFromEnum } from 'utils/generator';
import { loadDataSources } from 'utils/study-data.service';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import './add-field-styles.scss';

const initialState = {
    id: null,
    friendlyName: '',
    sourceFieldName: '',
    fieldType: null as any,
    table: null as any,
    isCalculated: false,
    active: true,
    configId: null,
};

const initialErrors = {
    id: '',
    friendlyName: '',
    sourceFieldName: '',
    fieldType: '',
    table: '',
    configId: '',
};

const AddField = ({ currentField, open, onClose, fields, studyId, isSysAdmin, isCM2Study }: AddFieldProps) => {
    const { t } = useTranslation();
    const [saveField] = useMutation(SAVE_EXTRA_FIELD);
    const [seriesRowDataOptions, setSeriesRowDataOptions] = useState([]);
    const [fieldTypes, setFieldTypes] = useState([]);
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const disabledSave = useBoolean(true);
    const [existingError, setExistingError] = useState(null);
    const { dispatch } = useContext(LocalStateContext);
    const client = useApolloClient();

    useEffect(() => {
        setSeriesRowDataOptions(
            createArrayFromEnum(RowDataSourceTable, '_TABLE', null).filter(
                (item: any) => item.id !== RowDataSourceTable.NONE && item.id !== RowDataSourceTable.STUDY,
            ),
        );

        setFieldTypes(
            createArrayFromEnum(FieldDefinitionFieldType, '_FIELD_TYPE', null).filter(
                (item: any) => item.id !== FieldDefinitionFieldType.DATE,
            ),
        );
    }, []);

    useEffect(() => {
        if (currentField)
            setState({
                id: currentField.id ?? state.id,
                friendlyName: currentField.friendlyName ?? state.friendlyName,
                sourceFieldName: currentField.sourceFieldName ?? state.sourceFieldName,
                table: currentField.table ?? state.table,
                fieldType: currentField.fieldType ?? state.fieldType,
                isCalculated: currentField.isCalculated ?? state.isCalculated,
                active: currentField.active ?? state.active,
                configId: currentField.configId ?? state.configId,
            });
    }, [currentField]);

    const addExistingErrors = () => {
        setExistingError(t('admin:displayNameAndOrDataSourceExists'));
        setErrors({
            ...errors,
            friendlyName: ' ',
            table: ' ',
        });
    };

    const removeExistingErrors = () => {
        setExistingError(null);
        setErrors({
            ...errors,
            friendlyName: null,
            table: null,
        });
    };

    const close = (action: string | boolean | undefined) => {
        if (action !== 'error') {
            setExistingError(null);
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        }
        disabledSave.setTrue();
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        const configIdError = checkConfigIdErrors(state, fields, value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors((prevErrors: typeof initialErrors) => ({
            ...prevErrors,
            ...error,
        }));
    };

    const onChange = (name: string, length?: number) => (event: any) => {
        const { value } = event.target;

        if ((name === 'friendlyName' || name === 'table') && existingError) removeExistingErrors();

        disabledSave.setFalse();

        setState({
            ...state,
            [name]: value,
        });
        validate(value, name, length);
    };

    const onCheckboxChange = (fieldName: any) => () => {
        disabledSave.setFalse();
        setState({ ...state, [fieldName]: !(state as any)[fieldName] });
    };

    const submit = () => {
        const requiredFields = ['friendlyName', 'sourceFieldName', 'table', 'fieldType'];

        if (currentField) {
            requiredFields.push('configId');
            const configIdError = checkConfigIdErrors(state, fields);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }

        const ifNameExists = checkIfNameExist(
            {
                ...state,
                friendlyName: state.friendlyName.trim(),
                id: currentField?.id ?? null,
            },
            fields,
            ['friendlyName', 'table'],
        );

        if (ifNameExists) {
            addExistingErrors();
            return;
        }

        const obj = {
            ...state,
            table: state.table === null ? '' : state.table,
            fieldType: state.fieldType === null ? '' : state.fieldType,
        };

        if (checkRequiredFields({ fields: requiredFields, state: obj, setErrors, errors }) && !ifNameExists) {
            saveField({
                variables: {
                    field: {
                        ...state,
                        id: currentField?.id ?? null,
                        studyId,
                    },
                    reason: '',
                },
            })
                .then(async () => {
                    await loadDataSources(client, dispatch, studyId);
                    close(currentField ? 'edit' : 'add');
                })
                .catch(() => {
                    close('error');
                });
        }
    };

    return (
        <CustomDialog className="add-field-dialog" onClose={close} fullWidth maxWidth="md" scroll="paper" open={open}>
            <DialogTitle id="edit-field-title">
                {currentField ? `${t('admin:editFieldDialog', { id: currentField?.recordId })}` : t('admin:addField')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close(false);
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent style={{ padding: 20, paddingBottom: 10 }}>
                {/* 5px padding to match the checkbox below */}
                <div className="checkboxes-wrapper" style={{ paddingBottom: '5px' }}>
                    <div className="left-part">
                        <Checkbox
                            rounded={null}
                            id="edit-field-active"
                            className="cell-checkbox no-border"
                            checked={state.active}
                            onChange={onCheckboxChange('active')}
                            label={t('admin:active')}
                            disabled={isSysAdmin}
                            data-testid="edit-field-active"
                            size="medium"
                        />
                    </div>
                </div>
                <div style={{ alignItems: 'flex-start', marginBottom: 0 }} className="type-edc-wrapper no-mb">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:displayName')}
                            id="edit-field-displayName"
                            fullWidth
                            required
                            value={state.friendlyName}
                            onChange={onChange('friendlyName', 50)}
                            placeholder={t('admin:enterDisplayName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-field-displayName-text"
                                    length={state.friendlyName.length}
                                    max={50}
                                    error={errors.friendlyName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.friendlyName}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <SelectWithCategories
                            label={`${t('admin:fieldType')}*`}
                            dataArray={fieldTypes}
                            onChange={onChange}
                            fieldSelect="fieldType"
                            currentState={state.fieldType}
                            labelField="fieldType"
                            showWarning={false}
                            filterable={false}
                            customLabelId="field-data-label-fieldType"
                            customId="field-data-fieldType"
                            customClassName="edit-atts-category"
                            error={errors.fieldType}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>
                <div className="checkboxes-wrapper">
                    {/* 5px padding so the label on the drop down below doesn't cover the checkbox */}
                    <div className="left-part" style={{ paddingBottom: '5px' }}>
                        <Checkbox
                            rounded={null}
                            id="edit-field-isCalculated"
                            className="cell-checkbox no-border"
                            checked={state.isCalculated}
                            onChange={onCheckboxChange('isCalculated')}
                            label={t('admin:isCalculated')}
                            disabled={isSysAdmin}
                            data-testid="edit-field-isCalculated"
                            size="medium"
                        />
                    </div>
                </div>
                <div className={cn('type-edc-wrapper', { 'no-mb': !!currentField })}>
                    {/* alignSelf so that the control appears at the top of the flexbox not the bottom */}
                    <FormControl variant="outlined" className="form-control-type mr" style={{ alignSelf: 'baseline' }}>
                        <SelectWithCategories
                            label={`${t('admin:dataSourceName')}*`}
                            dataArray={seriesRowDataOptions}
                            onChange={onChange}
                            fieldSelect="table"
                            currentState={state.table}
                            labelField="table"
                            showWarning={false}
                            filterable={false}
                            customLabelId="dataSourceName-label-id"
                            customId="dataSourceName-id"
                            customClassName="edit-atts-category"
                            error={errors.table}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:sourceColumnName')}
                            id="edit-field-sourceColumnName"
                            fullWidth
                            required
                            value={state.sourceFieldName}
                            onChange={onChange('sourceFieldName', 50)}
                            placeholder={t('admin:enterSourceColumnName')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-field-sourceColumnName-text"
                                    length={state.sourceFieldName?.length}
                                    max={50}
                                    error={errors.sourceFieldName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.sourceFieldName}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>

                {!!currentField && (
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type">
                            <TextField
                                readOnly={!isSysAdmin}
                                id="edit-field-config-id"
                                label={t('admin:configId')}
                                onChange={onChange('configId', 24)}
                                fullWidth
                                value={state.configId}
                                size="small"
                                InputProps={{
                                    readOnly: !isSysAdmin,
                                    endAdornment: <CopyButton value={state.configId ?? ''} />,
                                }}
                                helperText={
                                    <span style={{ color: '#ae0015' }} className="remaining-text">
                                        {errors.configId}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!errors.configId}
                            />
                        </FormControl>
                    </div>
                )}
                {existingError && <p className="addField__existingError">{existingError}</p>}
            </DialogContent>

            <DialogActions
                style={{ paddingRight: 20, paddingBottom: 20, paddingLeft: 20 }}
                className={`actions-info-wrapper-edit-cv ${!currentField ? 'flex-end' : ''}`}
            >
                <ModalUpdatedInfo item={currentField} />
                <Button
                    disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value}
                    text={currentField ? 'update' : 'add'}
                    id="edit-field-save-close"
                    onClick={submit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddField;
